import { useEffect, useRef, useState } from 'react';
import { Message } from '@/types/message.types';
import { useMessagesStore } from './useMessagesStore';
import { useMessagesStoreInit } from './useMessagesStoreInit';

export const useUnreadAgentMessage = () => {
  const {
    messages,
    lastAgentMessageId,
    lastReadAgentMessageId,
    hasUnreadAgentMessages,
    actions: messagesActions,
  } = useMessagesStore();
  const messagesStoreHydrated = useMessagesStoreInit();
  const hasUnreadAgentMessagesRef = useRef<boolean>(hasUnreadAgentMessages);
  const lastReadAgentMessageIdRef = useRef<Message['id'] | null>(
    lastReadAgentMessageId,
  );
  const [firstUnreadAgentMessageId, setFirstUnreadAgentMessageId] = useState<
    Message['id'] | null
  >(null);

  // update lastReadAgentMessageId for unread status updates - wait for hydration
  useEffect(() => {
    if (
      messagesStoreHydrated &&
      lastAgentMessageId &&
      lastAgentMessageId !== lastReadAgentMessageId
    ) {
      messagesActions.setLastReadAgentMessageId(lastAgentMessageId);
    }
  }, [
    messagesStoreHydrated,
    lastAgentMessageId,
    lastReadAgentMessageId,
    messagesActions,
  ]);

  // find and set the first agent message after the last read agent message once
  useEffect(() => {
    setFirstUnreadAgentMessageId((prevFirstUnreadAgentMessageId) => {
      if (
        !messagesStoreHydrated ||
        !hasUnreadAgentMessagesRef.current ||
        prevFirstUnreadAgentMessageId
      ) {
        return prevFirstUnreadAgentMessageId;
      }

      const lastReadAgentMessageIndex = messages.findIndex(
        (message) => message.id === lastReadAgentMessageIdRef.current,
      );

      if (lastReadAgentMessageIndex === -1) {
        return prevFirstUnreadAgentMessageId;
      }

      return (
        messages
          .slice(lastReadAgentMessageIndex + 1)
          .find((message) => message.isSentFromSleekflow)?.id || null
      );
    });
  }, [messagesStoreHydrated, messages]);

  return { firstUnreadAgentMessageId };
};
